import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiShield, FiKey, FiPlus, FiCheckCircle, FiAlertCircle, FiCpu, FiTrash2 } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import api from '../lib/api';
import toast from 'react-hot-toast';

export default function Fido2Enrollment() {
  const [loading, setLoading] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  /**
   * Hardware Key Enrollment - v2.29.0
   * Implements navigator.credentials.create() using FIDO2 CTAP2.
   */
  const enrollHardwareKey = async () => {
    setLoading(true);
    try {
      // 1. Get creation options from server
      const { data: options } = await api.post('/auth/mfa/webauthn/register-options');
      
      // 2. Prepare options for navigator.credentials.create
      const challenge = Uint8Array.from(atob(options.challenge), c => c.charCodeAt(0));
      const userId = Uint8Array.from(options.user.id, c => c.charCodeAt(0));

      const publicKeyCredentialCreationOptions = {
        publicKey: {
          ...options,
          challenge: challenge,
          user: {
            ...options.user,
            id: userId
          },
          authenticatorSelection: {
            userVerification: "required",
            residentKey: "preferred",
            requireResidentKey: false
          }
        }
      };

      // 3. Create Credential (Browser Native Handshake)
      const credential = await navigator.credentials.create(publicKeyCredentialCreationOptions);
      
      if (!credential) throw new Error("No hardware credential created");

      // 4. Send attestation response back to server
      await api.post('/auth/mfa/webauthn/register-verify', {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
        type: credential.type,
        attestation: {
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject))),
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON)))
        },
        challenge: options.challenge
      });

      setEnrolled(true);
      toast.success('Hardware key registered successfully');
    } catch (err) {
      console.error('FIDO2 Enrollment Error:', err);
      toast.error(err.name === 'NotAllowedError' ? "Enrollment cancelled" : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-[2.5rem] p-8">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h3 className="text-lg font-black flex items-center gap-2 text-white">
            <SafeIcon icon={FiKey} className="text-primary-500" /> Hardware Security Key
          </h3>
          <p className="text-xs text-slate-500 mt-1">v2.29 FIDO2/WebAuthn Protection • AAL3 Enforcement</p>
        </div>
        {enrolled && (
          <div className="bg-emerald-500/10 text-emerald-500 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
            <SafeIcon icon={FiCheckCircle} /> Active
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="bg-slate-800/20 border border-slate-800 p-6 rounded-2xl flex items-center gap-4 group hover:border-slate-700 transition-all">
          <div className="w-12 h-12 bg-slate-950 rounded-xl flex items-center justify-center text-primary-500 border border-slate-800">
            <SafeIcon icon={FiCpu} className="text-xl" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">YubiKey / Bio-Key / Titan</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black">CTAP2 Hardware Security Module</p>
          </div>
          {!enrolled ? (
            <button
              onClick={enrollHardwareKey}
              disabled={loading}
              className="bg-primary-600 hover:bg-primary-500 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest disabled:opacity-50 flex items-center gap-2 transition-all shadow-lg shadow-primary-900/20"
            >
              {loading ? 'Processing...' : <><SafeIcon icon={FiPlus} /> Enroll Key</>}
            </button>
          ) : (
            <button className="text-rose-500 text-[10px] font-black uppercase tracking-widest hover:text-rose-400 transition-colors flex items-center gap-1">
              <SafeIcon icon={FiTrash2} /> Revoke
            </button>
          )}
        </div>

        <div className="bg-primary-500/5 border border-primary-500/20 p-5 rounded-2xl flex gap-4">
          <SafeIcon icon={FiShield} className="text-primary-500 mt-1 shrink-0" />
          <div>
            <p className="text-[10px] font-black text-primary-400 uppercase tracking-widest">AAL3 Biometric Fallback Policy</p>
            <p className="text-[10px] text-slate-400 leading-relaxed mt-1">
              Primary authentication requires hardware presence (AAL3). Biometric fallback (AAL2) is permitted only for established devices during emergency sessions, subject to 7-day re-authentication.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}